import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';

import { Heading, LoadingSpinner, PageContainer, Section } from '../../../shared/ui';
import { appendYowanPreviewQueryIfNeeded } from '../../../app/host/yowanPreviewRouting';
import {
  fetchYowanNotificationUnsubscribe,
  updateYowanNotificationUnsubscribe,
} from '../app/yowanContentClient';
const { yowanPageContent } = require('./pageContent');
const { getYowanRouteMeta } = require('../routes/manifest');

const categoryLabels = {
  news: 'お知らせ',
  blog: 'ブログ',
};

const getPreferenceLabel = (preference) =>
  `${preference.categoryLabel}: ${preference.isEnabled ? '受信中' : '停止中'}`;

const mergeUnsubscribeBundle = (currentBundle, nextBundle, { category = '', all = false } = {}) => {
  if (!currentBundle) {
    return nextBundle;
  }

  const nextPreferences = Array.isArray(nextBundle?.preferences) && nextBundle.preferences.length
    ? nextBundle.preferences
    : (currentBundle.preferences || []).map((preference) => ({
        ...preference,
        isEnabled: all || preference.categoryKey === category ? false : preference.isEnabled,
      }));

  return {
    ...currentBundle,
    ...nextBundle,
    recipient: nextBundle?.recipient || currentBundle.recipient,
    preferences: nextPreferences,
  };
};

const NotificationUnsubscribePage = () => {
  const content = yowanPageContent['/notifications/unsubscribe'];
  const metadata = getYowanRouteMeta('/notifications/unsubscribe');
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const token = searchParams.get('token') || '';
  const category = searchParams.get('category') || '';
  const [bundle, setBundle] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isUpdating, setIsUpdating] = useState(false);
  const [message, setMessage] = useState('');
  const [loadError, setLoadError] = useState('');

  useEffect(() => {
    if (!metadata) {
      return;
    }

    document.title = metadata.title;

    if (metadata.description) {
      const descriptionTag = document.querySelector('meta[name="description"]');
      if (descriptionTag) {
        descriptionTag.setAttribute('content', metadata.description);
      }
    }
  }, [metadata]);

  useEffect(() => {
    let isMounted = true;
    setIsLoading(true);
    setMessage('');
    setLoadError('');

    if (!token) {
      setBundle(null);
      setLoadError('配信停止URLが正しくありません。');
      setIsLoading(false);
      return () => {
        isMounted = false;
      };
    }

    fetchYowanNotificationUnsubscribe({ token, category })
      .then((payload) => {
        if (isMounted) {
          setBundle(payload);
        }
      })
      .catch(() => {
        if (isMounted) {
          setBundle(null);
          setLoadError('配信停止情報を確認できませんでした。URLをご確認ください。');
        }
      })
      .finally(() => {
        if (isMounted) {
          setIsLoading(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [category, token]);

  if (!content) {
    return null;
  }

  const requestedCategoryLabel = categoryLabels[category] || category || '';
  const canStopCategory = Boolean(token && category);

  const handleUnsubscribe = async (all = false) => {
    if (!token) {
      return;
    }

    setIsUpdating(true);
    setMessage('');
    setLoadError('');

    try {
      const nextBundle = await updateYowanNotificationUnsubscribe({
        token,
        category,
        all,
      });
      setBundle((currentBundle) => mergeUnsubscribeBundle(currentBundle, nextBundle, { category, all }));
      setMessage(all ? 'すべての配信を停止しました。' : `${requestedCategoryLabel}の配信を停止しました。`);
    } catch (error) {
      setLoadError('配信停止を完了できませんでした。時間をおいて再度お試しください。');
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <Section>
      <PageContainer>
        <div className="yowan-mypage-update-shell">
          <div className="yowan-mypage-update-header">
            <Heading as="h1" align="left">
              {content.header.title}
            </Heading>
            {content.header.paragraphs.map((paragraph) => (
              <p key={paragraph} className="yowan-mypage-update-header__lead">
                {paragraph}
              </p>
            ))}
          </div>

          <section className="yowan-admin-form c-surface-content">
            {isLoading ? <LoadingSpinner /> : null}

            {!isLoading && loadError ? (
              <p className="yowan-mypage-update-form__error">{loadError}</p>
            ) : null}

            {!isLoading && bundle ? (
              <>
                <label className="c-form__field">
                  <span className="c-form__label">対象メールアドレス</span>
                  <input
                    className="c-form__control"
                    type="text"
                    value={bundle.recipient.maskedEmail || '-'}
                    readOnly
                  />
                </label>

                <div className="c-form__field">
                  <span className="c-form__label">現在の受信設定</span>
                  <div className="yowan-mypage-summary__grid">
                    {bundle.preferences.map((preference) => (
                      <p key={preference.categoryKey} className="yowan-mypage-update-form__message">
                        {getPreferenceLabel(preference)}
                      </p>
                    ))}
                  </div>
                </div>

                <div className="yowan-admin-form__actions">
                  {canStopCategory ? (
                    <button
                      type="button"
                      className="c-form__submit"
                      disabled={isUpdating}
                      onClick={() => handleUnsubscribe(false)}
                    >
                      {requestedCategoryLabel}の配信を停止
                    </button>
                  ) : null}
                  <button
                    type="button"
                    className="c-form__submit"
                    disabled={isUpdating}
                    onClick={() => handleUnsubscribe(true)}
                  >
                    すべての配信を停止
                  </button>
                </div>

                {message ? <p className="yowan-mypage-update-form__message">{message}</p> : null}
              </>
            ) : null}

            <Link
              to={appendYowanPreviewQueryIfNeeded('/', location.search)}
              className="yowan-mypage-section__action"
            >
              トップへ戻る
            </Link>
          </section>
        </div>
      </PageContainer>
    </Section>
  );
};

export default NotificationUnsubscribePage;
